import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Residential / Garage Cleanout",
  "Commercial / Office Cleanout",
  "Furniture & Appliance Removal",
  "Yard Debris & Storm Cleanup",
  "Full Estate / Hoarding Cleanout",
];

const inputCls =
  "w-full bg-black border border-zinc-800 rounded-xl px-4 py-3.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors";
const labelCls = "block text-xs font-mono uppercase tracking-wider text-zinc-300 mb-2";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", city: "", service: SERVICE_OPTIONS[0], details: "" });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    const next = [...photos, ...chosen].slice(0, 5);
    setPhotos(next);
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) {
      toast.error("Please add your name, phone, and city so our dispatcher can reach you.");
      return;
    }
    setSending(true);
    try {
      let photoPaths = [];
      if (photos.length > 0) {
        const data = new FormData();
        photos.forEach((p) => data.append("files", p));
        const up = await axios.post(`${API}/uploads`, data);
        photoPaths = up.data.files.map((f) => f.path);
      }
      await axios.post(`${API}/leads`, { ...form, photos: photoPaths });
      toast.success("Quote request received — our Brevard dispatcher will call you back shortly.");
      setForm({ name: "", phone: "", city: "", service: SERVICE_OPTIONS[0], details: "" });
      setPhotos([]);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us at (321) 420-2707.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-black border-t border-white/20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8 flex flex-col justify-between">
            <div>
              <span className="inline-block px-3 py-1 rounded bg-white text-black font-mono text-xs uppercase font-black tracking-wider mb-4">
                Direct Brevard Dispatch
              </span>
              <h2 className="text-4xl sm:text-6xl font-black uppercase tracking-tighter font-display text-white">
                Get Your Space Back Today.
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base mt-4 leading-relaxed">
                Call our local crew directly for an immediate estimate or same-day booking. Or fill out the short form to receive a prompt callback.
              </p>

              <div className="mt-8 p-6 rounded-2xl bg-zinc-950 border border-white/20">
                <div className="text-xs font-mono text-zinc-400 uppercase tracking-wider mb-1">Fastest Response Phone Line</div>
                <a
                  href="tel:3214202707"
                  data-testid="contact-call-number"
                  className="block text-3xl sm:text-4xl font-black text-white font-mono tracking-tight hover:text-zinc-300 transition-colors"
                >
                  (321) 420-2707
                </a>
                <p className="text-xs text-zinc-400 mt-2">Mon-Sat 7:00 AM - 7:00 PM • Sunday by Appointment</p>
                <div className="mt-4 pt-4 border-t border-zinc-800 text-xs font-mono text-zinc-300">
                  Email:{" "}
                  <a href="mailto:dumpd.junkremoval@gmail.com" data-testid="contact-email-link" className="hover:text-white transition-colors">
                    dumpd.junkremoval@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="text-xs font-mono text-zinc-400 space-y-1">
              <p>✓ No obligation free estimates</p>
              <p>✓ Zero-touch loading guarantee</p>
              <p>✓ Brevard County local crew</p>
            </div>
          </div>

          <div className="lg:col-span-7 bg-zinc-950 border border-zinc-800 rounded-3xl p-8 sm:p-10">
            <h3 className="text-2xl font-black uppercase tracking-tight text-white font-display mb-2">Request A Free Quote</h3>
            <p className="text-xs text-zinc-400 mb-8">Tell us what you need removed and our dispatcher will contact you immediately.</p>

            <form className="space-y-5" onSubmit={submit} data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="quote-name">Your Name</label>
                  <input id="quote-name" data-testid="quote-name-input" type="text" placeholder="John Doe" className={inputCls} value={form.name} onChange={set("name")} required />
                </div>
                <div>
                  <label className={labelCls} htmlFor="quote-phone">Phone Number</label>
                  <input id="quote-phone" data-testid="quote-phone-input" type="tel" placeholder="(321) 000-0000" className={inputCls} value={form.phone} onChange={set("phone")} required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="quote-city">Location / City</label>
                  <input id="quote-city" data-testid="quote-city-input" type="text" placeholder="Palm Bay, Melbourne, etc." className={inputCls} value={form.city} onChange={set("city")} required />
                </div>
                <div>
                  <label className={labelCls} htmlFor="quote-service">Service Needed</label>
                  <select id="quote-service" data-testid="quote-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="quote-details">Describe Items to Remove</label>
                <textarea
                  id="quote-details"
                  data-testid="quote-details-input"
                  rows="3"
                  placeholder="E.g. Old sofa, 5 bags of yard waste, garage clutter, broken washer..."
                  className={inputCls}
                  value={form.details}
                  onChange={set("details")}
                />
              </div>

              <div>
                <label className={labelCls}>Photos of Your Junk (optional, up to 5)</label>
                <input
                  id="quote-photos"
                  data-testid="quote-photos-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={addPhotos}
                />
                <label
                  htmlFor="quote-photos"
                  data-testid="quote-photos-btn"
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-700 text-xs font-bold uppercase tracking-wider text-white hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  + Add Photos ({photos.length}/5)
                </label>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3" data-testid="quote-photos-preview">
                    {photos.map((p, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                        <img src={URL.createObjectURL(p)} alt={`junk upload ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          data-testid={`quote-photo-remove-${i}`}
                          onClick={() => removePhoto(i)}
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/80 text-white text-[10px] leading-none flex items-center justify-center hover:bg-black"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 font-mono mt-2">Photos help us quote faster — snap the pile and attach.</p>
              </div>

              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={sending}
                className="w-full py-4 rounded-xl bg-white text-black font-black uppercase text-sm tracking-wider hover:bg-zinc-200 transition-colors shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? "Sending to Dispatch..." : "Submit For Free Brevard Estimate"}
              </button>

              <p className="text-[11px] text-zinc-400 text-center font-mono">
                We never share your details. Immediate phone estimates available at (321) 420-2707.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
